import { SAVE_KEYS, validateBlockJamSave } from '@puzzlemania/blockjam';

/**
 * Module 50 (WP-50.6) — which per-game validator guards which save key.
 *
 * `LocalSaveModule` is generic and lives in `packages/plugins`, which cannot
 * know what any game's save looks like; the games know their own shape but are
 * handed the object too late to refuse it. The composition root is the only
 * place that knows both, so the pairing lives here — and here rather than in
 * either composition root, because both of them (the main app and the
 * CrazyGames embed) load the same Block Jam save and a fix applied to one would
 * be a bug in the other.
 *
 * Deliberately Block-Jam-only. Adding Water Sort here would pull it into the
 * embed's module graph, which `platforms/crazygames.ts` forbids by name — a
 * validator registry is not worth breaking the profile boundary for. When Water
 * Sort needs one, it gets its own entry and the forbidden-deps check will say
 * so.
 */
const VALIDATORS: Readonly<Record<string, (raw: unknown) => unknown>> = Object.freeze({
  [SAVE_KEYS.BLOCKJAM_PROGRESS]: validateBlockJamSave,
});

/**
 * The validator for a save key, or `undefined` for a key with no validation —
 * which keeps that key's behaviour exactly what it was before this existed.
 *
 * The cast is the one place `T` is asserted. It is honest by construction: a
 * save module for key K is built to carry K's data, and the validator for K is
 * the function that produces it. `saveValidators.test.ts` pins the pairing.
 */
export function saveValidatorFor<T>(key: string): ((raw: unknown) => T | null) | undefined {
  return VALIDATORS[key] as ((raw: unknown) => T | null) | undefined;
}
